import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import {
  Button,
  IconButton,
  InputBase,
  Paper,
  Tooltip,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Close as CloseIcon,
  Typography,
} from "../../../common/utils/mui-imports";
import {
  Bag,
  CopySuccess,
  InfoCircle,
  MinusCirlce,
  SearchNormal1,
  TickCircle,
} from "iconsax-react";
import HPRCheckBox from "../../../components/common/HPRCheckBox/HPRCheckBox";
import { QuestionsTableProps } from "./QuestionsTable.types";
import {
  QuestionChoiceDTO,
  StandardQuestionUploadDTO,
} from "../../../common/types/common.dto.types";
import { useDispatch } from "react-redux";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import noData from "../../../images/no_data.svg";

// Virtual scrolling constants
const ROW_HEIGHT = 60; // Approximate row height
const VISIBLE_ROWS = 20; // Number of rows to render at once
const BUFFER_ROWS = 10; // Extra rows for smooth scrolling

// Memoized row component for better performance
const TableRowMemo = memo(
  ({
    row,
    selectedRowPageState,
    onRowClick,
    onCheckboxChange,
    onCopyClick,
    hoveredIcon,
    setHoveredIcon,
  }: {
    row: StandardQuestionUploadDTO;
    selectedRowPageState: string | null;
    onRowClick: (rowId: string, isSelected: boolean) => void;
    onCheckboxChange: (rowId: string, checked: boolean) => void;
    onCopyClick: (row: StandardQuestionUploadDTO) => void;
    hoveredIcon: string | null;
    setHoveredIcon: (id: string | null) => void;
  }) => {
    const isSelected = selectedRowPageState === row.RowId;

    return (
      <TableRow
        key={row.RowId}
        hover
        role="checkbox"
        onClick={(e) => {
          e.stopPropagation();
          if (selectedRowPageState !== row.RowId) {
            onRowClick(
              row.RowId,
              !selectedRowPageState || selectedRowPageState !== row.RowId
            );
          }
        }}
        aria-checked={isSelected}
        selected={isSelected}
        className={row.IsSelected === true ? "checkbox-selected" : null}
        sx={{
          cursor: isSelected ? "default" : "pointer",
          "&:hover": {
            backgroundColor: "#f8f9fa",
          },
        }}
      >
        <TableCell padding="checkbox">
          <HPRCheckBox
            Name={""}
            checked={row?.IsSelected}
            onCheckBoxClick={(event) => {
              event.stopPropagation();
              onCheckboxChange(row.RowId, event.target.checked);
            }}
          />
        </TableCell>
        <TableCell sx={{ fontWeight: 500, color: "#333" }}>
          <span>{row?.RowId}</span>
          {row?.IsWarning && (
            <Tooltip title={row?.Warning}>
              <span
                style={{
                  verticalAlign: "text-top",
                  display: "inline-block",
                  marginTop: "2px",
                  marginLeft: "5px",
                }}
              >
                <InfoCircle size="16" color="#FF9418" />
              </span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell
          sx={{ maxWidth: 300, wordWrap: "break-word", lineHeight: 1.4 }}
        >
          <Tooltip title={row.Requirement}>
            <span className="limit-3">{row?.Requirement}</span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          {row?.QuestionTypeName ?? "-"}
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          {row?.QuestionCategoryName !== "" && row?.QuestionCategoryName != null
            ? row?.QuestionCategoryName
            : "-"}
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {row.QuestionChoices?.some(
              (choice: QuestionChoiceDTO) => choice.IsGap
            ) ? (
              <Tooltip
                title={
                  <div>
                    {row.QuestionChoices.filter(
                      (choice: QuestionChoiceDTO) => choice.IsGap
                    ).map((gap: QuestionChoiceDTO, index) => (
                      <div key={index}>
                        {index + 1}. {gap.ChoiceText.DefaultText1}
                      </div>
                    ))}
                  </div>
                }
              >
                <span style={{ whiteSpace: "nowrap", position: "relative" }}>
                  {
                    row.QuestionChoices.filter(
                      (choice: QuestionChoiceDTO) => choice.IsGap
                    ).length
                  }{" "}
                  Gap(s) Selected
                  <InfoCircle
                    style={{
                      height: "18px",
                      position: "absolute",
                      transform: "rotate(180deg)",
                    }}
                    color="#219EBC"
                  />
                </span>
              </Tooltip>
            ) : (
              "-"
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          <Tooltip
            title={
              row.IncludeNotApplicable
                ? "Yes"
                : row.IncludeNotApplicable === false
                ? "No"
                : ""
            }
          >
            <span>
              {row.IncludeNotApplicable ? (
                <TickCircle size="20" color="#6b9b37" />
              ) : row.IncludeNotApplicable === false ? (
                <MinusCirlce size="20" color="#666" />
              ) : (
                "-"
              )}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell sx={{ textAlign: "left" }}>
          <Tooltip
            title={
              row.AllowComments
                ? "Yes"
                : row.AllowComments === false
                ? "No"
                : ""
            }
          >
            <span>
              {row.AllowComments ? (
                <TickCircle size="20" color="#6b9b37" />
              ) : row.AllowComments === false ? (
                <MinusCirlce size="20" color="#666" />
              ) : (
                "-"
              )}
            </span>
          </Tooltip>
        </TableCell>
        <TableCell padding="checkbox">
          <Tooltip title={row?.IsWarning ? "" : "Copy Details"}>
            <Box
              onMouseEnter={() => setHoveredIcon(row.RowId)}
              onMouseLeave={() => setHoveredIcon(null)}
              sx={{ display: "inline-flex" }}
            >
              <IconButton
                onClick={(e) => {
                  if (!row?.IsWarning) {
                    e.stopPropagation();
                    onCopyClick(row);
                  }
                }}
                sx={{
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(33, 158, 188, 0.04)",
                    cursor: row?.IsWarning ? "default" : "pointer",
                  },
                }}
              >
                <CopySuccess
                  size="32"
                  color={
                    hoveredIcon === row.RowId && !row?.IsWarning
                      ? "#219ebc"
                      : row.RowId === selectedRowPageState && !row?.IsWarning
                      ? "#219ebc"
                      : "#D9D9D9"
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  }
);

const QuestionsTable = (props: QuestionsTableProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isAllClicked, setIsAllClicked] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const [selectedRowPageState, setSelectedRowPageState] = useState<
    string | null
  >(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [isApplySearch, setIsApplySearch] = useState(false);

  const dispatch = useDispatch();

  // Memoized filtered data to prevent unnecessary recalculations
  const filteredData = useMemo(() => {
    if (!props.QuestionsData || props.QuestionsData.length === 0) return [];

    if (isApplySearch && searchTerm !== "") {
      return props.QuestionsData.filter((q) =>
        q.Requirement?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return props.QuestionsData;
  }, [props.QuestionsData, isApplySearch, searchTerm]);

  // Memoized question details to prevent unnecessary state updates
  const questionDetails = useMemo(() => filteredData, [filteredData]);

  // Virtual scrolling calculations
  const totalHeight = questionDetails.length * ROW_HEIGHT;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS
  );
  const endIndex = Math.min(
    questionDetails.length,
    Math.ceil((scrollTop + VISIBLE_ROWS * ROW_HEIGHT) / ROW_HEIGHT) +
      BUFFER_ROWS
  );
  const visibleData = questionDetails.slice(startIndex, endIndex);
  const offsetY = startIndex * ROW_HEIGHT;

  // Memoized handlers to prevent unnecessary re-renders
  const handleRowClick = useCallback(
    (rowId: string, isSelected: boolean) => {
      let selectedRow = selectedRowPageState;
      if (isSelected) {
        selectedRow = rowId;
        setSelectedRowPageState(rowId);
      } else {
        selectedRow = null;
        setSelectedRowPageState(null);
      }

      if (selectedRow != null) {
        const data = questionDetails.find((q) => q.RowId === rowId);
        props.onChangeSelectRow(data);
      } else {
        props.onChangeSelectRow(null);
      }
    },
    [selectedRowPageState, questionDetails, props]
  );

  const handleCheckboxChange = useCallback(
    (rowId: string, checked: boolean) => {
      const updatedDetails = questionDetails.map((q) =>
        q.RowId === rowId ? { ...q, IsSelected: checked } : q
      );

      const question = updatedDetails.find((q) => q.RowId === rowId);
      if (question) {
        props.onChangeIsSelected(question ? [question] : [], false);
      }

      const selectedRows = updatedDetails
        .filter((q) => q.IsSelected)
        .map((q) => q.RowId);
      setSelectedQuestions(selectedRows);
    },
    [questionDetails, props]
  );

  const handleCopyClick = useCallback(
    (row: StandardQuestionUploadDTO) => {
      setIsClicked(!isClicked);
      props.onCopyPopupOpen(row);
    },
    [isClicked, props]
  );

  // Optimized search handlers
  const handleSearchValueChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (value === "") {
      setIsApplySearch(false);
    }
  }, []);

  const handleSearchChange = useCallback(() => {
    setIsApplySearch(true);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
    setIsApplySearch(false);
  }, []);

  const handleAllCheckboxChange = useCallback(
    (name: string, value: boolean) => {
      props.onChangeIsSelected(
        questionDetails.map((q) => ({ ...q, IsSelected: value })),
        value
      );

      if (value) {
        setSelectedQuestions(questionDetails.map((q) => q.RowId));
      } else {
        setSelectedQuestions([]);
      }
    },
    [questionDetails, props]
  );

  const handleDelete = useCallback(() => {
    if (selectedQuestions.length !== 0) {
      props.onChangeDelete(selectedQuestions);
    }
  }, [selectedQuestions, props]);

  // Scroll handler for virtualization
  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  // Update selected questions when data changes
  useEffect(() => {
    if (props.QuestionsData && props.QuestionsData.length > 0) {
      const filterIsSelectedValues = props.QuestionsData.filter(
        (i) => i.IsSelected
      ).map((i) => i.RowId);
      setSelectedQuestions(filterIsSelectedValues);

      if (props.SelectedRowId != null) {
        setSelectedRowPageState(props.SelectedRowId);
      } else {
        setSelectedRowPageState(null);
      }

      const allSelected =
        questionDetails.length > 0 &&
        questionDetails.every((i) => i.IsSelected);
      setIsAllClicked(allSelected);
    }
  }, [props.QuestionsData, props.SelectedRowId, questionDetails]);

  const showDeleteButton = useMemo(
    () => questionDetails?.some((some) => some.IsSelected ?? false),
    [questionDetails]
  );

  return (
    <>
      {/* Left Panel - Questions Table */}
      <div
        className="d-flex fx-between"
        style={{ marginRight: "10px", display: "block" }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}
          className="search-bar--small"
        >
          <Paper
            component="form"
            sx={{
              p: "2px 4px",
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
            onMouseEnter={() => setIsMouseOver(true)}
            onMouseLeave={() => setIsMouseOver(false)}
          >
            <InputBase
              value={searchTerm ?? ""}
              onChange={(event) => {
                event.stopPropagation();
                handleSearchValueChange(event.target?.value);
              }}
              onKeyPress={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleSearchChange();
                }
              }}
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search by Requirement"
              inputProps={{ "aria-label": "search" }}
            />

            {isMouseOver && searchTerm && searchTerm.length > 0 && (
              <Tooltip title="Clear">
                <IconButton className="mr-0" onClick={handleClearSearch}>
                  <CloseIcon style={{ color: "#219ebc", fontSize: "20px" }} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Search">
              <IconButton
                type="button"
                sx={{ p: "10px" }}
                aria-label="search"
                className="m-0"
                onClick={handleSearchChange}
              >
                <SearchNormal1
                  variant="Outline"
                  className="ico-secondary"
                  size={18}
                />
              </IconButton>
            </Tooltip>
          </Paper>

          {showDeleteButton && (
            <div className="ml-10">
              <Tooltip title="Delete" placement="bottom">
                <Button
                  aria-label="Cancel"
                  className="border-btn"
                  startIcon={<Bag />}
                  onClick={handleDelete}
                >
                  <span className="body-bold secondary-color">Delete</span>
                </Button>
              </Tooltip>
            </div>
          )}
        </div>

        <Typography variant="body2" style={{ marginTop: "7px" }}>
          <div className="body3">
            *Note: Please review and save your requirements as draft before
            logging out or leaving this page. If you leave the review page
            without saving, all uploaded details will be lost.
          </div>
        </Typography>
      </div>

      <div
        className="bulk-upload-table"
        style={{
          height: "calc(100% - 78px)",
          padding: "10px 10px 10px 0",
        }}
      >
        <TableContainer
          sx={{ maxHeight: "100%", overflowY: "auto" }}
          onScroll={handleScroll}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                style={{
                  padding: "20px 5px !important",
                  borderRight: "1px solid #b42b2b !important",
                  borderRadius: "5px",
                }}
              >
                <TableCell
                  sx={{ backgroundColor: "#D8EDF6" }}
                  padding="checkbox"
                >
                  <HPRCheckBox
                    Name={""}
                    checked={isAllClicked}
                    indeterminateChecked={
                      questionDetails?.some((q) => q.IsSelected) &&
                      !questionDetails?.every((q) => q.IsSelected)
                    }
                    onCheckBoxChange={handleAllCheckboxChange}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 70,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  <span>ID</span>
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 300,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Requirement
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 80,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Type
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 80,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Choice
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 150,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Gap(s)
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 120,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Include N/A
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 130,
                    borderRight: "1px solid #ddd",
                  }}
                >
                  Allow Comments
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    backgroundColor: "#D8EDF6",
                    minWidth: 70,
                  }}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Virtual scrolling spacer */}
              <TableRow style={{ height: offsetY, visibility: "hidden" }}>
                <TableCell colSpan={9} style={{ padding: 0, border: 0 }} />
              </TableRow>

              {/* Only render visible rows */}
              {visibleData.map((row: StandardQuestionUploadDTO) => (
                <TableRowMemo
                  key={row.RowId}
                  row={row}
                  selectedRowPageState={selectedRowPageState}
                  onRowClick={handleRowClick}
                  onCheckboxChange={handleCheckboxChange}
                  onCopyClick={handleCopyClick}
                  hoveredIcon={hoveredIcon}
                  setHoveredIcon={setHoveredIcon}
                />
              ))}

              {/* Bottom spacer for virtual scrolling */}
              <TableRow
                style={{
                  height:
                    totalHeight - offsetY - visibleData.length * ROW_HEIGHT,
                  visibility: "hidden",
                }}
              >
                <TableCell colSpan={9} style={{ padding: 0, border: 0 }} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {questionDetails.length === 0 && (
          <div className="data-wrapper">
            <div className="no-data width100-p flex--container">
              <img className="" src={noData} style={{}} alt="No data" />
              <div className="gray-text body2 flex__item--vcenter nodata-text">
                No records found
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default memo(QuestionsTable);
